/**
 * Shared scaffolding for klinecharts template registration. Every overlay /
 * indicator module previously copy-pasted the same three fragments:
 *
 *  - `let registered = false` + early-return guard      → `registerOnce`
 *  - `as Parameters<typeof registerOverlay>[0]` cast    → `OverlayTemplateParam`
 *    (and its `registerIndicator` twin)                 → `IndicatorTemplateParam`
 *  - the `needDefault*Figure` boolean triple            → `DEFAULT_FIGURES` /
 *                                                         `NO_DEFAULT_FIGURES`
 *  - the SPECS-array registration loop (talib, maBand)  → `registerIndicatorSpecs`
 *
 * IMPORTANT: this module must never import klinecharts at runtime — unit tests
 * mock `klinecharts` with factories that export only the register function the
 * module under test uses (`vi.mock('klinecharts', () => ({ registerIndicator }))`),
 * and a runtime import here would demand every export on every mock. Types are
 * imported type-only (erased), and `registerIndicatorSpecs` takes the caller's
 * own register function instead of importing it.
 */

import type { KLineData, registerIndicator, registerOverlay } from 'klinecharts'

/** The template parameter type of klinecharts' `registerOverlay`. Named so the
 *  per-file `as Parameters<typeof registerOverlay>[0]` cast reads as intent. */
export type OverlayTemplateParam = Parameters<typeof registerOverlay>[0]

/** The template parameter type of klinecharts' `registerIndicator`. */
export type IndicatorTemplateParam = Parameters<typeof registerIndicator>[0]

/**
 * Wrap a registration body in a call-once guard. Replaces the per-file
 * `let registered = false` / `if (registered) return` boilerplate:
 *
 *   export const registerFooOverlay = registerOnce(() => { registerOverlay(…) })
 *
 * The guard flips BEFORE the body runs (matching the old inline order), so a
 * throwing registration is not retried.
 */
export function registerOnce(fn: () => void): () => void {
  let registered = false
  return () => {
    if (registered) return
    registered = true
    fn()
  }
}

/** Spread into an overlay template that keeps klinecharts' default point /
 *  axis-label figures (draggable anchors + axis price/time pills). */
export const DEFAULT_FIGURES = {
  needDefaultPointFigure: true,
  needDefaultXAxisFigure: true,
  needDefaultYAxisFigure: true,
} as const

/** Spread into an overlay template that draws everything itself. */
export const NO_DEFAULT_FIGURES = {
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: false,
} as const

/**
 * Declarative indicator spec — the shape the TA-Lib and MA/band catalogs list
 * their indicators in. `registerIndicatorSpecs` turns each into a klinecharts
 * template.
 */
export interface IndicatorSpec {
  name: string
  shortName: string
  calcParams: number[]
  precision?: number
  shouldFormatBigNumber?: boolean
  /** Set for main-pane (price-scale) indicators; omitted → own sub-pane. */
  series?: 'price'
  figures: Array<{ key: string; title: string; type: 'line' | 'bar' }>
  calc: (dataList: KLineData[], params: number[]) => Array<Record<string, number | undefined>>
}

/** Catalog-wide fallbacks for fields a spec omits (the spec always wins). */
export interface IndicatorSpecDefaults {
  series?: 'price'
  precision?: number
}

/**
 * Register a catalog of indicator specs (idempotence is the caller's concern —
 * wrap the call in `registerOnce`). `register` is the caller's own
 * `registerIndicator` import so this module stays klinecharts-free at runtime.
 * Returns the spec names in registration order (picker/test manifests).
 */
export function registerIndicatorSpecs(
  register: typeof registerIndicator,
  specs: readonly IndicatorSpec[],
  defaults: IndicatorSpecDefaults = {},
): string[] {
  for (const spec of specs) {
    const series = spec.series ?? defaults.series
    register({
      name: spec.name,
      shortName: spec.shortName,
      calcParams: spec.calcParams,
      precision: spec.precision ?? defaults.precision,
      shouldFormatBigNumber: spec.shouldFormatBigNumber,
      series,
      shouldOhlc: series === 'price' ? true : undefined,
      figures: spec.figures,
      calc: (dataList: KLineData[], indicator: { calcParams: unknown[] }) =>
        spec.calc(dataList, indicator.calcParams as number[]),
    } as unknown as IndicatorTemplateParam)
  }
  return specs.map((s) => s.name)
}
